import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";

import { ConfirmPopup } from "../../../../Common";
import DraggableTable from "../../../../Common/DraggableTable";
import { ruleGroupRowHeader } from "../data";

const useStyles = makeStyles()((theme) => ({
  Typography: {
    marginTop: "15px",
    marginBottom: "0",
    color: "#df672a",
    fontSize: "16px",
  },
  DragHandle: {
    cursor: "move !important",
    color: "#000000de",
    "&:active": {
      color: "#096dd9",
    },
    "&:hover": {
      color: "#40a9ff",
    },
    "&:active,:focus,:hover": {
      outline: 0,
      textDecoration: "none",
    },
  },
  TextCenter: {
    textAlign: "center !important",
  },
}));

const Rules = ({ rules, title, handleRuleEditor, type, setRules, sourceObj }) => {
  const { classes } = useStyles();

  const [deleteID, setDeleteID] = useState(false);

  const handleDeletePopup = useCallback((id) => setDeleteID(id), []);

  const formatText = useCallback((text) => {
    const items = text.map(({ method, fields }) => {
      const fieldNames = fields.map(({ name }) => name).join("+");
      return `${ruleGroupRowHeader[method]}(${fieldNames})`;
    });

    return items.flatMap((item, index) =>
      index < items.length - 1
        ? [
            item,
            <span style={{ color: "rgba(0,0,0,0.45)" }} key={index}>
              {" "}
              AND{" "}
            </span>,
          ]
        : [item]
    );
  }, []);

  const deleteRule = useCallback(
    (id) => {
      setRules((prev) => prev.filter(({ id: ruleID }) => ruleID !== id));
      handleDeletePopup(false);
    },
    [handleDeletePopup, setRules]
  );

  const onChangeEvent = useCallback(
    (data) => {
      let { action, dataSource } = data;
      if (action === "drag") setRules(dataSource);
      return [];
    },
    [setRules]
  );

  const columns = useMemo(
    () => [
      {
        dataIndex: "drag",
        title: "",
        render: (value, record, index) => (
          <span className={classes.DragHandle}>
            <MenuOutlined />
          </span>
        ),
        className: classes.TextCenter,
        width: 46,
      },
      {
        dataIndex: "name",
        title: "Name",
        width: 200,
        render: (value, record, index) => <Typography variant="body2">{value}</Typography>,
        className: classes.TextCenter,
      },
      {
        dataIndex: "groups",
        title: "Rule",
        width: 200,
        render: (value, record, index) => <Typography variant="body2">{formatText(value)}</Typography>,
        className: classes.TextCenter,
      },
      {
        dataIndex: "sources",
        title: "Sources",
        width: 150,
        render: (value, record, index) => (
          <Typography variant="body2">{value.map((id) => (id === "Any" ? "Any" : sourceObj[id])).join(",")}</Typography>
        ),
        className: classes.TextCenter,
      },
      {
        dataIndex: "enabled",
        title: "Enabled",
        width: 60,
        render: (value, record, index) => <Typography variant="body2">{value ? "Yes" : "No"}</Typography>,
      },
      {
        dataIndex: "Action",
        title: "",
        width: 125,
        render: (value, record, index) => {
          return (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                className={classes.IconHolder}
                onClick={() => {
                  handleRuleEditor({ type, ...record });
                }}
              >
                <EditOutlinedIcon color="primary" fontSize="inherit" />
              </IconButton>
              <IconButton
                className={classes.IconHolder}
                onClick={() => handleDeletePopup(record.id)}
                style={{
                  marginLeft: "5px",
                  color: "#C72C2C",
                }}
              >
                <DeleteOutlineOutlinedIcon fontSize="inherit" />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [
      classes.DragHandle,
      classes.IconHolder,
      classes.TextCenter,
      formatText,
      handleDeletePopup,
      handleRuleEditor,
      sourceObj,
      type,
    ]
  );

  const RenderTable = useCallback(
    () => <DraggableTable data={rules} column={columns} rowSelection={false} onChange={onChangeEvent} />,
    [columns, onChangeEvent, rules]
  );

  return (
    <>
      <Typography className={classes.Typography} variant="subtitle2" gutterBottom component="div">
        {title}
      </Typography>
      <RenderTable />
      {deleteID && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Rules Confirmation",
            popupMessage: "Are you sure you want to delete the selected rules?",
            handleCancel: () => handleDeletePopup(false),
            handleClick: () => deleteRule(deleteID),
            loadingForConfirm: false,
            confirmBtnName: "Ok",
          }}
        />
      )}
    </>
  );
};

Rules.propTypes = {
  rules: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  handleRuleEditor: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  setRules: PropTypes.func.isRequired,
  sourceObj: PropTypes.object.isRequired,
};

export default Rules;
